import { Suspense, useCallback, useMemo, useState } from "react"
import { DateTime } from "luxon"
import { MenuItem, Typography } from "@mui/material"
import { faShop } from "@fortawesome/pro-light-svg-icons"
import { MRT_Row, MRT_ColumnDef } from "material-react-table"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import useActiveFiltersCountAccounting from "../../../../../../shared/components/filters/accountingFiltersDrawer/useActiveFiltersCountAccounting"
import { CompaniesOrdersResponse, CompanyReportsAccountingQuery, fetchCompaniesAccountingPosOrders } from "../posApi"
import { OrderIdInput } from "../../../../../supplier/reporting/accounting/components/inputFilters/OrderIdInput"
import { useExportPosOrders } from "../../../../../supplier/reporting/accounting/pos/hooks/useExportPosOrders"
import { StandardExportButton } from "../../../../../../shared/components/table/StandardExportButton"
import { StandardTwoLinesCell } from "../../../../../../shared/components/table/StandardTwoLinesCell"
import { StandardFilterButton } from "../../../../../../shared/components/table/StandardFilterButton"
import { StandardTableFilter } from "../../../../../../shared/components/table/StandardTableFilters"
import {
    AccountingFilterProps,
    AccountingFiltersDrawer,
    defaultAccountingFilterOptionsList,
} from "../../../../../../shared/components/filters/accountingFiltersDrawer/AccountingFiltersDrawer"
import { CompaniesOrderItem } from "../posTypes"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { QueryErrorBoundary } from "../../../../../../core/api/components/QueryErrorBoundary"
import { LoadingSpinner } from "../../../../../../shared/components/loading/LoadingSpinner"
import { useApiQueryTable } from "../../../../../../core/api/components/ApiQueryTable"
import { PageHeader } from "../../../../../../shared/components/pageHeader/PageHeader"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { Receipt, ReceiptDialog } from "../../../../../receipt/pages/ReceiptDialog"
import { Page } from "../../../../../../shared/components/pageHeader/PageHeaderApi"
import { COMPANY_REPORTS_ROUTE } from "../../../../../../constants/routes"
import { useCurrentCompany } from "../../../../hooks/useCurrentCompany"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../../shared/components/links/StandardBreadcrumbs"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import {
    TableNumberOfItemsBox,
    TableSummaryWrap,
    TableTotalAmountBox,
} from "../../../../../../shared/components/table/TableSummaryBox"
import {
    StandardDateRangeFilter,
    type DtDateRange,
} from "../../../../../../shared/components/table/StandardDateRangeFilter"
import { useInitialDateFilters } from "../../../../../../shared/components/table/useInitialDateFilters"

type Props = Readonly<{}>

function transformData(data: CompaniesOrdersResponse) {
    return data.orders
}

export function PosOrders({}: Props) {
    const { l10n } = useAppLocalization()
    const [receipt, setReceipt] = useState<Receipt>()
    const { companyId } = useCurrentCompany()
    const [drawerOpen, setDrawerOpen] = useState(false)
    const { startDate, endDate } = useInitialDateFilters()
    const [filtersList, setFiltersList] = useState<AccountingFilterProps>({
        ...defaultAccountingFilterOptionsList,
    })
    const [dateState, setDateState] = useState<DtDateRange>({
        startDate,
        endDate,
    })
    const { activeCount } = useActiveFiltersCountAccounting(filtersList)

    // An order id identifies the order uniquely, so the search must not be narrowed by the
    // period - the user searches by id precisely because the date is unknown.
    const isSearchingByOrderId = Boolean(filtersList.orderId)

    const companyOrders = useApiQuery(fetchCompaniesAccountingPosOrders, {
        queryName: CompanyReportsAccountingQuery.POS_ORDERS_FOR_COMPANY,
        dependencies: {
            ...filtersList,
            companyId,
            merchantIds: filtersList.merchantIds?.join(","),
            shopIds: filtersList.shopIds?.join(","),
            paymentMethods: filtersList.paymentMethods?.join(","),
            startDate: isSearchingByOrderId ? undefined : dateState.startDate?.toISODate(),
            endDate: isSearchingByOrderId ? undefined : dateState.endDate?.toISODate(),
        },
        select: transformData,
    })

    const { ApiQueryTable, columnFactory } = useApiQueryTable(companyOrders)

    const columns = useMemo<MRT_ColumnDef<CompaniesOrderItem>[]>(
        () => [
            columnFactory.idColumn({
                header: l10n.getString("accounting-pos-table-header-order-id"),
                accessorKey: "id",
            }),
            columnFactory.datetimeColumn(
                {
                    header: l10n.getString("accounting-pos-table-header-transaction-time"),
                    accessorKey: "created",
                },
                (row) => row.created,
            ),
            columnFactory.nameColumn({
                header: l10n.getString("accounting-pos-table-header-employee"),
                accessorKey: "customer",
                Cell: ({ row }) => (
                    <StandardTwoLinesCell
                        title={
                            <>
                                <Typography variant="body2" component="span" textTransform="uppercase">
                                    {row.original.customer.initials}
                                    {", "}
                                </Typography>
                                <Typography variant="body2" component="span">
                                    {row.original.customer.givenName} {row.original.customer.surname}
                                </Typography>
                            </>
                        }
                        subtitle1={row.original.customer.employeeId}
                        subtitle2={row.original.customer.email}
                    />
                ),
            }),
            columnFactory.shortNameColumn({
                header: l10n.getString("accounting-pos-table-header-payment"),
                accessorKey: "paymentDetails.paymentMethod",
            }),
            columnFactory.moneyColumn(
                {
                    header: l10n.getString("accounting-pos-table-header-amount"),
                    accessorKey: "amount.amount",
                },
                (row) => row.amount,
            ),
        ],
        [l10n, columnFactory],
    )

    const renderRowActionMenuItems = useCallback(
        ({ row, closeMenu }: { row: MRT_Row<CompaniesOrderItem>; closeMenu: () => void }) => [
            <MenuItem
                key={`show_receipt-${row.original.id}`}
                onClick={() => {
                    showReceipt(row.original)
                    closeMenu()
                }}
            >
                <LocalizedStrict id="support-entity-table-button-show-receipt">
                    <span>Show receipt</span>
                </LocalizedStrict>
            </MenuItem>,
        ],
        [],
    )

    function showReceipt(row: CompaniesOrderItem) {
        setReceipt({
            orderId: row.id,
            orderUid: row.uid,
            orderType: row.orderType,
        })
    }

    const exportOrder = useExportPosOrders({
        data: companyOrders.data,
        startDate: dateState.startDate || DateTime.now(),
        endDate: dateState.endDate || DateTime.now(),
    })

    return (
        <>
            <LocalizedStrict id="accounting-pos-table-header-title" attrs={{ title: true }}>
                <PageHeader
                    page={Page.COMPANY_ACCOUNTING_POS_ORDERS}
                    title="POS"
                    icon={<FontAwesomeIcon icon={faShop} />}
                >
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="accounting-pos-table-header-nav-item-home">
                            <BreadcrumbLink to={COMPANY_REPORTS_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="accounting-pos-table-header-nav-item-accounting">
                            <Breadcrumb>Accounting</Breadcrumb>
                        </LocalizedStrict>
                        <LocalizedStrict id="accounting-pos-table-header-nav-item-pos">
                            <Breadcrumb>POS</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>
            <QueryErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                    <ApiQueryTable
                        columns={columns}
                        initialState={{ density: "spacious" }}
                        renderRowActionMenuItems={renderRowActionMenuItems}
                        renderTopToolbarCustomActions={() => (
                            <StandardTableFilter variant="left">
                                <StandardDateRangeFilter value={dateState} onChange={setDateState} />

                                <OrderIdInput
                                    value={filtersList.orderId ?? ""}
                                    onChange={(orderId) => {
                                        setFiltersList((prev) => ({
                                            ...prev,
                                            orderId,
                                        }))
                                    }}
                                />
                            </StandardTableFilter>
                        )}
                        renderToolbarInternalActions={({ table }) => (
                            <StandardTableFilter variant="right" table={table}>
                                <StandardFilterButton onClick={() => setDrawerOpen(true)} activeCount={activeCount} />
                                <StandardExportButton
                                    onClick={() => {
                                        exportOrder()
                                    }}
                                />
                            </StandardTableFilter>
                        )}
                        renderBottomToolbarCustomActions={({ table }) => (
                            <TableSummaryWrap>
                                <LocalizedStrict id="table-summary-box-number-of-orders" attrs={{ title: true }}>
                                    <TableNumberOfItemsBox
                                        data={table.getFilteredRowModel().rows}
                                        isLoading={companyOrders.isLoading}
                                        title="Number of orders"
                                    />
                                </LocalizedStrict>

                                <TableTotalAmountBox
                                    data={table.getFilteredRowModel().rows}
                                    isLoading={companyOrders.isLoading}
                                    accessor={(row) => row.original.amount}
                                />
                            </TableSummaryWrap>
                        )}
                    />
                </Suspense>
            </QueryErrorBoundary>
            {receipt && (
                <ReceiptDialog
                    open={receipt !== undefined}
                    orders={companyOrders.data}
                    onClose={() => setReceipt(undefined)}
                    receipt={receipt}
                />
            )}

            <AccountingFiltersDrawer
                setFiltersList={setFiltersList}
                drawerOpen={drawerOpen}
                setDrawerOpen={setDrawerOpen}
                filtersList={filtersList}
            />
        </>
    )
}
